import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { pageview } from './utils/analytics';
import './theme/globalStyles.css';
import './theme/mobileOptimizations.css';

// Theme Context
import { ColorModeProvider } from './contexts/ThemeContext';

// Auth Context
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Public Pages — eager (needed for initial paint by unauthenticated visitors)
import Home from './pages/Home';
import HomeOriginal from './pages/HomeOriginal';
import HowItWorks from './pages/HowItWorks';
import About from './pages/About';
import HelpCenter from './pages/HelpCenter';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import RefundPolicy from './pages/RefundPolicy';
import ChooseUsername from './pages/ChooseUsername';
import Login from './pages/Login';
import Register from './pages/Register';
import NotFound from './pages/NotFound';

// Auth Components — eager (part of the sign-up / OAuth flow)
import LoginTest from './components/auth/LoginTest';
import OAuthHandler from './components/auth/OAuthHandler';
import SelectUserType from './pages/SelectUserType';
import AdminRoute from './components/auth/AdminRoute';
import AddRoleForm from './components/auth/AddRoleForm';
import SwitchToRole from './pages/SwitchToRole';

// Public Layout — eager (wraps all public routes)
import Layout from './components/layout/Layout';

// ─── Lazy chunks ────────────────────────────────────────────────────────────
// These are split into separate JS bundles and only downloaded when the user
// navigates to a route that needs them.  Public visitors never pay the cost.

// Brand portal (downloaded after brand login)
const BrandLayout        = lazy(() => import('./components/layout/BrandLayout'));
const BrandHome          = lazy(() => import('./pages/brand/BrandHome'));
const BrandDashboard     = lazy(() => import('./pages/brand/BrandDashboard'));
const DiscoverInfluencers = lazy(() => import('./pages/brand/DiscoverInfluencers'));
const CampaignManagement = lazy(() => import('./pages/brand/CampaignManagement'));
const CampaignDetail     = lazy(() => import('./pages/brand/CampaignDetail'));
const Settings           = lazy(() => import('./pages/brand/Settings'));
const Profile            = lazy(() => import('./pages/brand/Profile'));

// Influencer portal (downloaded after influencer login)
const InfluencerLayout   = lazy(() => import('./components/layout/InfluencerLayout'));
const InfluencerDashboard   = lazy(() => import('./pages/influencer/InfluencerDashboard'));
const InfluencerOnboarding  = lazy(() => import('./pages/influencer/InfluencerOnboarding'));
const InfluencerAccount     = lazy(() => import('./pages/influencer/InfluencerAccount'));
const InfluencerCampaigns   = lazy(() => import('./pages/influencer/InfluencerCampaigns'));
const InfluencerPayments    = lazy(() => import('./pages/influencer/InfluencerPayments'));
const SearchCampaigns       = lazy(() => import('./pages/influencer/SearchCampaigns'));

// Admin portal (downloaded only for admin emails)
const AdminLayout      = lazy(() => import('./components/layout/AdminLayout'));
const AdminClaimsPage  = lazy(() => import('./pages/admin/AdminClaimsPage'));

// Communication (shared between brand + influencer — downloaded on first message click)
const CommunicationHub = lazy(() => import('./components/communication/CommunicationHub'));

// Public profiles (rarely the first page a visitor lands on)
const InfluencerPublicProfile = lazy(() => import('./pages/public/InfluencerPublicProfile'));
const CreatorProfile          = lazy(() => import('./pages/public/CreatorProfile'));

// ─── Analytics ──────────────────────────────────────────────────────────────
// Fires a GA4 pageview on every React Router v6 route change.
// Must be rendered inside the Router context (which lives in index.js).
function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);
  return null;
}

// ─── Loading fallback ───────────────────────────────────────────────────────
// Pure CSS spinner — no external deps so it renders instantly while the chunk
// is downloading.
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <div style={{
      width: 40,
      height: 40,
      border: '3px solid #e5e7eb',
      borderTopColor: '#7b1fa2',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

function App() {
  return (
    <ColorModeProvider>
      <AuthProvider>
        <PageTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="original" element={<HomeOriginal />} />
              <Route path="how-it-works" element={<HowItWorks />} />
              <Route path="about" element={<About />} />
              <Route path="help" element={<HelpCenter />} />
              <Route path="privacy" element={<PrivacyPolicy />} />
              <Route path="terms" element={<TermsAndConditions />} />
              <Route path="refund-policy" element={<RefundPolicy />} />
              <Route path="login" element={<Login />} />
              <Route path="login/oauth" element={<OAuthHandler />} />
              <Route path="register" element={<Register />} />
              <Route path="select-user-type" element={<SelectUserType />} />
              <Route path="choose-username" element={<ChooseUsername />} />
              <Route path="test-auth" element={<LoginTest />} />

              {/* Multi-profile routes */}
              <Route path="add-role" element={<AddRoleForm />} />
              <Route path="switch-to/:role" element={<SwitchToRole />} />

              {/* Public influencer profiles */}
              <Route path="u/:userId" element={<InfluencerPublicProfile />} />
              {/* Unified creator profile (claimed + unclaimed) */}
              <Route path="creator/:creatorId" element={<CreatorProfile />} />
            </Route>

            {/* Protected Brand Routes */}
            <Route element={<ProtectedRoute userType="brand" />}>
              <Route path="/brand" element={<BrandLayout />}>
                <Route index element={<BrandHome />} />
                <Route path="discover" element={<DiscoverInfluencers />} />
                <Route path="campaigns" element={<CampaignManagement />} />
                <Route path="campaigns/:id" element={<CampaignDetail />} />
                <Route path="communication" element={<CommunicationHub />} />
                <Route path="analytics" element={<BrandDashboard />} />
                <Route path="billing" element={<div>Billing Page</div>} />
                <Route path="settings" element={<Settings />} />
                <Route path="profile" element={<Profile />} />
              </Route>
            </Route>

            {/* Protected Influencer Routes */}
            <Route element={<ProtectedRoute userType="influencer" />}>
              <Route path="/influencer" element={<InfluencerLayout />}>
                <Route index element={<InfluencerDashboard />} />
                <Route path="onboarding" element={<InfluencerOnboarding />} />
                <Route path="account" element={<InfluencerAccount />} />
                <Route path="campaigns" element={<InfluencerCampaigns />} />
                <Route path="communication" element={<CommunicationHub />} />
                {/* <Route path="learning" element={<div>Learning Center Page</div>} /> */}
                {/* <Route path="community" element={<div>Community Forum Page</div>} /> */}
                <Route path="search-campaigns" element={<SearchCampaigns />} />
                <Route path="payments" element={<InfluencerPayments />} />
                {/* <Route path="search-brands" element={<div>Search Brands Page</div>} /> */}
                {/* <Route path="certifications" element={<div>Certifications Page</div>} /> */}
              </Route>
            </Route>

            {/* Admin Routes — email allowlist guard */}
            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminClaimsPage />} />
              </Route>
            </Route>

            {/* Catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </ColorModeProvider>
  );
}

export default App;
